<template>
  <div class="form-provider">
    <slot />
  </div>
</template>

<script setup lang="ts">
import type { PageData } from '@/common/composables/usePageData'
import {
  PageDataKey,
  PageDataSlotKey,
  PageRegistryFormIdKey,
  PageWindowInstanceIdKey,
} from '@/common/composables/usePageData'
import { resolvePageIdentity } from '@/common/utils/resolvePageIdentity'
import { useAppStore } from '@/stores/app'
import { computed, inject, provide } from 'vue'
import { FormProviderKey } from './injection-keys'

interface Props {
  formId?: string
  slotId?: string
  registryFormId?: string
  windowInstanceId?: string
}

const props = defineProps<Props>()
const appStore = useAppStore()

// Получаем FormProvider из GlobalProvider
const formProvider = inject(FormProviderKey, null)

const slotId = computed(() => String(props.slotId ?? props.formId ?? '').trim())

// Получаем данные страницы по slotId
const pageDataValue = computed(() => {
  void appStore.pagesDataRevision
  if (!slotId.value) {
    console.warn('❌ FormProvider: no slotId provided')
    return {} as PageData
  }

  const data = appStore.pagesData[slotId.value]
  if (data && typeof data === 'object') {
    return data as PageData
  }

  return {} as PageData
})

const pageIdentity = computed(() =>
  resolvePageIdentity(pageDataValue.value as Record<string, any>, {
    slotId: slotId.value,
    registryFormId: props.registryFormId,
    windowInstanceId: props.windowInstanceId,
  }),
)

// Предоставляем данные страницы дочерним компонентам
provide(PageDataKey, () => pageDataValue.value)
provide(PageDataSlotKey, () => pageIdentity.value.slotId)
provide(PageRegistryFormIdKey, () => pageIdentity.value.registryFormId)
provide(PageWindowInstanceIdKey, () => pageIdentity.value.windowInstanceId)

// Передаем FormProvider дальше
if (formProvider) {
  provide(FormProviderKey, formProvider)
}
</script>

<style scoped lang="scss">
.form-provider {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  width: 100%;
}
</style>
